<template>
  <div class="auth-page">
    <DialogContainer v-if="isLoginFailed">
      <div class="adm-dialog">
        <div class="adm-dialog-header danger">
          <h3>Login failed</h3>
          <button
            type="button"
            class="adm-dialog-close"
            aria-label="Close"
            @click="toggleIsLoginFailed"
          >
            <i class="fa fa-times" />
          </button>
        </div>
        <div class="adm-dialog-body">
          <p>{{ loginErrorMessage }}</p>
        </div>
        <div class="adm-dialog-footer">
          <button class="adm-btn-primary" @click="toggleIsLoginFailed">
            Try again
          </button>
        </div>
      </div>
    </DialogContainer>

    <div class="auth-card">
      <div class="auth-brand">
        <img :src="brandLogo" alt="PreEvnt" class="auth-logo" />
        <div class="auth-brand-text">
          <span class="auth-brand-name">PreEvnt</span>
          <span class="auth-brand-sub">Admin Panel</span>
        </div>
      </div>

      <h1 class="auth-title">Welcome back</h1>
      <p class="auth-subtitle">Sign in to manage devices, users and tests.</p>

      <form @submit.prevent="handleSubmit" class="auth-form">
        <div class="auth-field">
          <label for="email" class="auth-label">Email</label>
          <div class="auth-input-wrap">
            <i class="fa fa-envelope auth-input-icon" aria-hidden="true" />
            <input
              id="email"
              v-model="form.email"
              type="email"
              class="adm-input auth-input"
              placeholder="you@example.com"
              autocomplete="email"
              :class="{ 'is-error': errors.email }"
              required
            />
          </div>
          <span v-if="errors.email" class="auth-error">{{ errors.email }}</span>
        </div>

        <div class="auth-field">
          <label for="password" class="auth-label">Password</label>
          <div class="auth-input-wrap">
            <i class="fa fa-lock auth-input-icon" aria-hidden="true" />
            <input
              id="password"
              v-model="form.password"
              :type="passwordType"
              class="adm-input auth-input has-trailing"
              placeholder="••••••••"
              autocomplete="current-password"
              :class="{ 'is-error': errors.password }"
              required
            />
            <button
              type="button"
              class="auth-input-trailing"
              :aria-label="form.passwordVisible ? 'Hide password' : 'Show password'"
              @click="togglePasswordVisibility"
            >
              <i :class="eyeIconClass" />
            </button>
          </div>
          <span v-if="errors.password" class="auth-error">{{ errors.password }}</span>
        </div>

        <div class="auth-row">
          <label class="auth-remember">
            <input v-model="rememberMe" type="checkbox" />
            <span>Remember me</span>
          </label>
          <router-link to="/forgotpassword" class="auth-forgot-link">
            Forgot password?
          </router-link>
        </div>

        <button
          type="submit"
          class="adm-btn-primary auth-submit"
          :disabled="showLoading"
        >
          <i v-if="showLoading" class="fa fa-spinner fa-spin" />
          <i v-else class="fa fa-right-to-bracket" />
          {{ showLoading ? "Signing in…" : "Sign in" }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_BASE_URL } from "@/utils/apiBase";
import { startSession } from "@/utils/authStorage";
import DialogContainer from "@/components/DialogContainer.vue";
import BRAND_LOGO from "@/assets/adm-panel-logo.png";

export default {
  components: { DialogContainer },
  data() {
    return {
      brandLogo: BRAND_LOGO,
      form: {
        email: "",
        password: "",
        passwordVisible: false,
      },
      errors: {
        email: "",
        password: "",
      },
      rememberMe: false,
      isLoginFailed: false,
      loginErrorMessage: "",
      showLoading: false,
    };
  },
  computed: {
    passwordType() {
      return this.form.passwordVisible ? "text" : "password";
    },
    eyeIconClass() {
      return this.form.passwordVisible ? "fas fa-eye-slash" : "fas fa-eye";
    },
  },
  methods: {
    togglePasswordVisibility() {
      this.form.passwordVisible = !this.form.passwordVisible;
    },
    validateForm() {
      this.errors.email = this.form.email ? "" : "Email is required";
      this.errors.password = this.form.password ? "" : "Password is required";
    },
    handleSubmit() {
      this.validateForm();
      if (this.errors.email || this.errors.password) return;
      this.showLoading = true;

      axios
        .post(`${API_BASE_URL}/login`, {
          admin_users_email: this.form.email,
          admin_users_password: this.form.password,
          remember_me: this.rememberMe,
        })
        .then((response) => {
          startSession(
            {
              authToken: response.data.token,
              userRole: response.data.user.role_id,
              fullName: response.data.user.name,
              userID: response.data.user.id,
              profileImageURL: `/storage/${response.data.user.profile_photo}`,
            },
            { remember: this.rememberMe }
          );
          this.$router.push("/dashboard");
        })
        .catch((error) => {
          const data = error.response && error.response.data;
          console.error("Login failed:", data || error.message);
          this.loginErrorMessage =
            (data && data.message) ||
            (data &&
              data.errors &&
              Object.values(data.errors).flat().join(" ")) ||
            (error.message
              ? `Cannot reach server: ${error.message}`
              : "Invalid credentials.");
          this.isLoginFailed = true;
          this.showLoading = false;
        });
    },
    toggleIsLoginFailed() {
      this.isLoginFailed = false;
    },
  },
};
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f4f6fa;
  padding: 1.5rem;
}

.auth-card {
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border: 1px solid #e6e8ee;
  border-radius: 14px;
  padding: 2rem 2rem 1.75rem;
  box-shadow: 0 10px 32px rgba(16, 24, 40, 0.08);
}

.auth-brand {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.25rem;
}
.auth-logo {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  object-fit: contain;
}
.auth-brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}
.auth-brand-name {
  font-weight: 800;
  font-size: 1rem;
  color: #1f2540;
}
.auth-brand-sub {
  font-size: 0.7rem;
  color: #98a0ad;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.auth-title {
  margin: 0;
  font-size: 1.45rem;
  font-weight: 700;
  color: #1f2540;
}
.auth-subtitle {
  margin: 0.25rem 0 1.5rem;
  font-size: 0.88rem;
  color: #6b7383;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.auth-field {
  display: flex;
  flex-direction: column;
}
.auth-label {
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #5b6577;
  margin-bottom: 0.35rem;
}

.auth-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.auth-input-icon {
  position: absolute;
  left: 0.85rem;
  color: #98a0ad;
  font-size: 0.85rem;
  pointer-events: none;
}
.auth-input {
  width: 100%;
  padding-left: 2.25rem;
}
.auth-input.has-trailing {
  padding-right: 2.5rem;
}
.auth-input.is-error,
.auth-input.is-error:focus {
  border-color: #b02a37;
  box-shadow: 0 0 0 3px rgba(176, 42, 55, 0.15);
}
.auth-input-trailing {
  position: absolute;
  right: 0.35rem;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: transparent;
  border: none;
  color: #5b6577;
  cursor: pointer;
}
.auth-input-trailing:hover {
  background: #eef0f4;
  color: #2a345d;
}
.auth-error {
  color: #b02a37;
  font-size: 0.78rem;
  margin-top: 0.3rem;
}

.auth-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.15rem;
  font-size: 0.82rem;
}
.auth-remember {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: #5b6577;
  cursor: pointer;
  user-select: none;
  margin: 0;
}
.auth-remember input[type="checkbox"] {
  width: 14px;
  height: 14px;
  accent-color: #2a345d;
}
.auth-forgot-link {
  color: #2a345d;
  font-weight: 500;
  text-decoration: none;
}
.auth-forgot-link:hover {
  text-decoration: underline;
}

.auth-submit {
  width: 100%;
  justify-content: center;
  margin-top: 0.5rem;
}

/* --- failure dialog uses the global .adm-dialog skin --- */
.adm-dialog {
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(16, 24, 40, 0.18);
  overflow: hidden;
}
.adm-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.1rem;
  background: #f7f8fb;
  border-bottom: 1px solid #eaecf2;
}
.adm-dialog-header.danger {
  background: #fdecee;
  border-bottom-color: #f7c8cf;
}
.adm-dialog-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2540;
}
.adm-dialog-header.danger h3 {
  color: #b02a37;
}
.adm-dialog-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #5b6577;
  cursor: pointer;
}
.adm-dialog-close:hover {
  background: rgba(42, 52, 93, 0.08);
  color: #2a345d;
}
.adm-dialog-body {
  padding: 1.1rem 1.25rem;
}
.adm-dialog-body p {
  margin: 0;
  color: #5b6577;
  font-size: 0.9rem;
}
.adm-dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-top: 1px solid #eaecf2;
}
</style>
